/**
 * Recurring Expenses Management Page
 * 
 * Allows users to view, edit, pause/resume, and delete recurring expense templates.
 * Shows template details, next due date, and generated expense history.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecurringExpenseService } from '@/lib/domains/recurring';
import { CategoryService } from '@/lib/domains/category';
import type { RecurringExpense, CategoryViewModel } from '@/lib/domains';
import { formatCurrency } from '@/lib/utils/currency';
import { Calendar, Repeat, Pause, Play, Trash2, ChevronLeft, Clock, Edit } from 'lucide-react';
import { RecurringExpenseEditModal } from '@/components/recurring-expense-edit-modal';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { ListSkeleton } from '@/components/skeleton';

export default function RecurringExpensesPage() {
    const navigate = useNavigate();
    const [recurring, setRecurring] = useState<RecurringExpense[]>([]);
    const [categories, setCategories] = useState<CategoryViewModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        loadRecurringExpenses();
    }, []);

    const loadRecurringExpenses = async () => {
        try {
            setLoading(true);

            // Get user ID first
            const { getSupabase } = await import('@/lib/database');
            const supabase = getSupabase();
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) throw new Error('Not authenticated');

            const [recurringData, cats] = await Promise.all([
                RecurringExpenseService.getRecurringExpenses(),
                CategoryService.getVisibleCategoriesWithPresentation(user.id, 'light')
            ]);

            setRecurring(recurringData);
            setCategories(cats);
            setError(null);
        } catch (err: unknown) {
            console.error('Failed to load recurring expenses:', err);
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load recurring expenses'
            );
        } finally {
            setLoading(false);
        }
    };

    const getCategoryInfo = (categoryId?: string): CategoryViewModel | null => {
        if (!categoryId) return null;
        return categories.find(c => c.id === categoryId) || null;
    };

    const handleToggleActive = async (id: string) => {
        try {
            await RecurringExpenseService.toggleActive(id);
            await loadRecurringExpenses();
            setSuccessMessage('Recurring expense updated');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to update recurring expense'
            );
        }
    };

    const handleEdit = async (id: string, updates: Partial<RecurringExpense>) => {
        // Store original state for revert on error
        const originalRecurring = [...recurring];

        // Optimistic update - update UI immediately
        setRecurring(prev => prev.map(r =>
            r.id === id ? { ...r, ...updates } : r
        ));

        try {
            await RecurringExpenseService.updateRecurringExpense(id, updates);
            setSuccessMessage('Recurring expense updated successfully');
            setTimeout(() => setSuccessMessage(null), 3000);
        } catch (err: unknown) {
            // Revert on error
            setRecurring(originalRecurring);
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to update recurring expense'
            );
            throw err; // Re-throw to modal can handle
        }
    };

    const handleDelete = async () => {
        if (!deletingId) return;

        setIsDeleting(true);
        try {
            await RecurringExpenseService.deleteRecurringExpense(deletingId);
            await loadRecurringExpenses();
            setSuccessMessage('Recurring expense deleted');
            setTimeout(() => setSuccessMessage(null), 3000);
            setDeletingId(null);
        } catch (err: unknown) {
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to delete recurring expense'
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const getDaysUntil = (dateStr: string): number => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(dateStr + 'T00:00:00');
        const diffTime = dueDate.getTime() - today.getTime();
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getDueDateLabel = (dateStr: string): string => {
        const days = getDaysUntil(dateStr);
        if (days === 0) return 'Due today';
        if (days === 1) return 'Due tomorrow';
        if (days < 0) return `Overdue (${Math.abs(days)} days)`;
        if (days <= 7) return `Due in ${days} days`;
        return `Due ${new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        })}`;
    };

    const activeRecurring = recurring.filter(r => r.isActive);
    const pausedRecurring = recurring.filter(r => !r.isActive);

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-4 py-6">
                    <button
                        onClick={() => navigate('/settings')}
                        className="flex items-center gap-2 text-teal-600 hover:text-teal-700 mb-3 font-medium"
                    >
                        <ChevronLeft className="w-5 h-5" />
                        Back to Settings
                    </button>
                    <h1 className="text-3xl font-bold text-gray-900">Recurring Expenses</h1>
                    <p className="text-gray-600 mt-1">Manage your automatic expense templates</p>
                </div>
            </div>

            {/* Success Message */}
            {successMessage && (
                <div className="max-w-4xl mx-auto px-4 pt-4">
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
                        {successMessage}
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="max-w-4xl mx-auto px-4 pt-4">
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                        {error}
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                {loading ? (
                    <ListSkeleton count={3} />
                ) : recurring.length === 0 ? (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                        <Repeat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            No recurring expenses yet
                        </h3>
                        <p className="text-gray-600 mb-6">
                            Set up automatic expense tracking for bills and subscriptions
                        </p>
                        <button
                            onClick={() => navigate('/add')}
                            className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700"
                        >
                            Create Recurring Expense
                        </button>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Active Recurring Expenses */}
                        {activeRecurring.length > 0 && (
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <Play className="w-5 h-5 text-green-600" />
                                    Active ({activeRecurring.length})
                                </h2>
                                <div className="space-y-4">
                                    {activeRecurring.map((recurring) => {
                                        const category = getCategoryInfo(recurring.categoryId);
                                        const daysUntil = getDaysUntil(recurring.nextDueDate);

                                        return (
                                            <div
                                                key={recurring.id}
                                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start gap-4 flex-1">
                                                        {category && (
                                                            <span className="text-3xl">{category.icon}</span>
                                                        )}
                                                        <div className="flex-1">
                                                            <h3 className="font-semibold text-lg text-gray-900 mb-1">
                                                                {category?.displayName || 'Other'}
                                                            </h3>
                                                            {recurring.description && (
                                                                <p className="text-gray-600 mb-2">{recurring.description}</p>
                                                            )}
                                                            <div className="flex flex-wrap gap-4 text-sm">
                                                                <div className="flex items-center gap-1.5 text-gray-600">
                                                                    <Repeat className="w-4 h-4" />
                                                                    {RecurringExpenseService.getFrequencyDisplayName(recurring.frequency)}
                                                                </div>
                                                                <div className={`flex items-center gap-1.5 font-medium ${daysUntil < 0 ? 'text-red-600' :
                                                                    daysUntil === 0 ? 'text-orange-600' :
                                                                        daysUntil <= 3 ? 'text-yellow-600' :
                                                                            'text-gray-600'
                                                                    }`}>
                                                                    <Calendar className="w-4 h-4" />
                                                                    {getDueDateLabel(recurring.nextDueDate)}
                                                                </div>
                                                                {recurring.endDate && (
                                                                    <div className="flex items-center gap-1.5 text-gray-500">
                                                                        <Clock className="w-4 h-4" />
                                                                        Ends {new Date(recurring.endDate).toLocaleDateString()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-bold text-xl text-gray-900 mb-3">
                                                            {formatCurrency(recurring.amount, recurring.currency)}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => setEditingId(recurring.id)}
                                                                className="px-3 py-1.5 text-sm bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-lg transition-colors flex items-center gap-1.5"
                                                                aria-label={`Edit ${category?.displayName || 'recurring'} expense`}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleToggleActive(recurring.id)}
                                                                className="px-3 py-1.5 text-sm bg-yellow-100 hover:bg-yellow-200 text-yellow-700 rounded-lg transition-colors flex items-center gap-1.5"
                                                                aria-label={`Pause ${category?.displayName || 'recurring'} expense`}
                                                            >
                                                                <Pause className="w-4 h-4" />
                                                                Pause
                                                            </button>
                                                            <button
                                                                onClick={() => setDeletingId(recurring.id)}
                                                                className="px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                                                                aria-label={`Delete ${category?.displayName || 'recurring'} expense`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Paused Recurring Expenses */}
                        {pausedRecurring.length > 0 && (
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                    <Pause className="w-5 h-5 text-gray-500" />
                                    Paused ({pausedRecurring.length})
                                </h2>
                                <div className="space-y-4">
                                    {pausedRecurring.map((recurring) => {
                                        const category = getCategoryInfo(recurring.categoryId);

                                        return (
                                            <div
                                                key={recurring.id}
                                                className="bg-gray-50 rounded-lg shadow-sm border border-gray-200 p-6 opacity-75"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex items-start gap-4 flex-1">
                                                        {category && (
                                                            <span className="text-3xl grayscale">{category.icon}</span>
                                                        )}
                                                        <div className="flex-1">
                                                            <h3 className="font-semibold text-lg text-gray-700 mb-1">
                                                                {category?.displayName || 'Other'}
                                                            </h3>
                                                            {recurring.description && (
                                                                <p className="text-gray-500 mb-2">{recurring.description}</p>
                                                            )}
                                                            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                                                                <div className="flex items-center gap-1.5">
                                                                    <Repeat className="w-4 h-4" />
                                                                    {RecurringExpenseService.getFrequencyDisplayName(recurring.frequency)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-bold text-xl text-gray-700 mb-3">
                                                            {formatCurrency(recurring.amount, recurring.currency)}
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => setEditingId(recurring.id)}
                                                                className="px-3 py-1.5 text-sm bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-lg transition-colors flex items-center gap-1.5"
                                                                aria-label={`Edit ${category?.displayName || 'recurring'} expense`}
                                                            >
                                                                <Edit className="w-4 h-4" />
                                                                Edit
                                                            </button>
                                                            <button
                                                                onClick={() => handleToggleActive(recurring.id)}
                                                                className="px-3 py-1.5 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors flex items-center gap-1.5"
                                                                aria-label={`Resume ${category?.displayName || 'recurring'} expense`}
                                                            >
                                                                <Play className="w-4 h-4" />
                                                                Resume
                                                            </button>
                                                            <button
                                                                onClick={() => setDeletingId(recurring.id)}
                                                                className="px-3 py-1.5 text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                                                                aria-label={`Delete ${category?.displayName || 'recurring'} expense`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editingId && recurring.find(r => r.id === editingId) && (
                <RecurringExpenseEditModal
                    recurring={recurring.find(r => r.id === editingId)!}
                    categories={categories}
                    onSave={(updates) => handleEdit(editingId, updates)}
                    onCancel={() => setEditingId(null)}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deletingId !== null}
                title="Delete Recurring Expense?"
                message="This will delete the template. Previously generated expenses will remain unchanged."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDelete}
                onCancel={() => setDeletingId(null)}
                isDestructive
                isLoading={isDeleting}
            />
        </div>
    );
}
